import React, { useEffect, useState } from "react";
import api, { fmtMoney, fmtDate, formatApiErrorDetail } from "@/lib/api";
import MetricCard from "@/components/MetricCard";
import ShareTaxSavingsModal from "@/components/ShareTaxSavingsModal";
import { Calculator, TrendingDown, Receipt, Calendar, Check, Sparkles, Share2 } from "lucide-react";
import { toast } from "sonner";
import {
  Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";

export default function TaxSavings() {
  const [summary, setSummary] = useState(null);
  const [opps, setOpps] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showShare, setShowShare] = useState(false);

  const load = async () => {
    setLoading(true);
    const [s, o, ss] = await Promise.all([
      api.get("/tax/summary"),
      api.get("/tax/opportunities"),
      api.get("/tax/settings"),
    ]);
    setSummary(s.data); setOpps(o.data); setSettings(ss.data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const updateSettings = async (patch) => {
    try {
      const { data } = await api.patch("/tax/settings", patch);
      setSettings(data);
      toast.success("Settings updated");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (loading || !summary) return <div className="p-8 text-ink-lighter">Loading…</div>;

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto" data-testid="tax-page">
      <div className="mb-8 flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="overline mb-2">Tax Savings · {summary.tax_year}</div>
          <h1 className="font-heading text-4xl font-medium tracking-tight">Keep what's yours.</h1>
          <p className="text-ink-light mt-2">Every deductible dollar tracked. Estimated tax in real time.</p>
        </div>
        <button
          onClick={() => setShowShare(true)}
          disabled={summary.tax_savings_from_deductions <= 0}
          data-testid="open-share-modal"
          className="btn-forest inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed self-start md:self-auto"
          title={summary.tax_savings_from_deductions <= 0 ? "Track deductible expenses first" : "Share your tax win"}
        >
          <Share2 className="w-4 h-4" /> Share my savings
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <MetricCard label="YTD Income" value={fmtMoney(summary.income)} testid="tax-metric-income" icon={Receipt} tone="teal" />
        <MetricCard label="Deductible Expenses" value={fmtMoney(summary.deductible_expenses)} testid="tax-metric-deductions" accent="text-forest" icon={Check} tone="forest"
          sub={`${((summary.deductible_expenses / Math.max(1, summary.total_expenses)) * 100).toFixed(0)}% of total expenses`} />
        <MetricCard label="Est. Tax Owed" value={fmtMoney(summary.estimated_tax)} testid="tax-metric-owed" accent="text-ochre" icon={Calculator} tone="ochre"
          sub={`Taxable income · ${fmtMoney(summary.taxable_income)}`} />
        <MetricCard label="Tax Saved" value={fmtMoney(summary.tax_savings_from_deductions)} testid="tax-metric-saved" accent="text-forest" icon={TrendingDown} tone="plum"
          sub="From deductions" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mb-6">
        <div className="lg:col-span-2 card-surface p-6" data-testid="deductions-by-category">
          <div className="overline mb-1.5">Deductions by Category</div>
          <h3 className="font-heading text-2xl font-medium mb-5">Where you're saving</h3>
          {summary.deductions_by_category.length === 0 ? (
            <div className="text-sm text-ink-lighter">No deductible expenses yet. Mark expenses as deductible on the Transactions page.</div>
          ) : (
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={summary.deductions_by_category} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E3" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11, fill: "#8A8A8A" }} tickLine={false} axisLine={false} tickFormatter={(v) => "$" + (v/1000).toFixed(0) + "k"} />
                  <YAxis dataKey="category" type="category" width={110} tick={{ fontSize: 12, fill: "#525252" }} tickLine={false} axisLine={false} />
                  <Tooltip formatter={(v) => fmtMoney(v)} contentStyle={{ background: "#fff", border: "1px solid #E5E5E3", borderRadius: 8, fontSize: 12 }} />
                  <Bar dataKey="amount" fill="#0F4C3A" radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        <div className="card-surface p-6" data-testid="quarterly-card">
          <div className="overline mb-1.5">Quarterly Estimated</div>
          <h3 className="font-heading text-2xl font-medium mb-5">Pay-as-you-go schedule</h3>
          <div className="space-y-4">
            {summary.quarterly.map((q) => (
              <div key={q.q} className="flex items-center justify-between py-2 border-b border-border last:border-0" data-testid={`quarter-${q.q}`}>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-md bg-forest/8 text-forest flex items-center justify-center">
                    <Calendar className="w-4 h-4" strokeWidth={1.75} />
                  </div>
                  <div>
                    <div className="font-medium text-sm">Q{q.q}</div>
                    <div className="text-xs text-ink-lighter num">Due {fmtDate(q.due)}</div>
                  </div>
                </div>
                <div className="num text-base font-medium">{fmtMoney(q.amount)}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="card-surface p-6 mb-6" data-testid="tax-settings-card">
        <div className="overline mb-1.5">Settings</div>
        <h3 className="font-heading text-2xl font-medium mb-5">Tax profile</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <label className="text-sm">
            <div className="font-medium mb-1.5">Business type</div>
            <select value={settings?.business_type || "llc"} onChange={(e) => updateSettings({ business_type: e.target.value })}
              data-testid="business-type-select"
              className="w-full bg-white border border-border rounded-lg px-3 py-2 focus:ring-2 focus:ring-forest/20 focus:border-forest outline-none">
              <option value="sole_prop">Sole Proprietor</option>
              <option value="llc">LLC</option>
              <option value="s_corp">S-Corp</option>
              <option value="c_corp">C-Corp</option>
              <option value="partnership">Partnership</option>
            </select>
          </label>
          <label className="text-sm">
            <div className="font-medium mb-1.5">Effective tax rate</div>
            <select value={settings?.effective_tax_rate || 0.25} onChange={(e) => updateSettings({ effective_tax_rate: parseFloat(e.target.value) })}
              data-testid="tax-rate-select"
              className="w-full bg-white border border-border rounded-lg px-3 py-2 focus:ring-2 focus:ring-forest/20 focus:border-forest outline-none num">
              <option value="0.10">10%</option>
              <option value="0.12">12%</option>
              <option value="0.15">15%</option>
              <option value="0.18">18%</option>
              <option value="0.22">22%</option>
              <option value="0.25">25%</option>
              <option value="0.28">28%</option>
              <option value="0.32">32%</option>
              <option value="0.37">37%</option>
            </select>
          </label>
          <label className="text-sm">
            <div className="font-medium mb-1.5">State</div>
            <input value={settings?.state || ""} onChange={(e) => updateSettings({ state: e.target.value })}
              data-testid="state-input"
              className="w-full bg-white border border-border rounded-lg px-3 py-2 focus:ring-2 focus:ring-forest/20 focus:border-forest outline-none uppercase"
              maxLength={2} placeholder="CA" />
          </label>
        </div>
      </div>

      <div className="mb-3">
        <div className="overline mb-2">Opportunities</div>
        <h3 className="font-heading text-2xl font-medium tracking-tight">Deductions you might be missing</h3>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        {opps.map((o) => (
          <div key={o.name} className="card-surface p-5 flex gap-4" data-testid={`opportunity-${o.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            <div className="shrink-0 w-9 h-9 rounded-lg bg-ochre/15 text-ochre flex items-center justify-center">
              <Sparkles className="w-4 h-4" strokeWidth={1.75} />
            </div>
            <div className="flex-1">
              <div className="flex items-start justify-between gap-2 mb-1.5">
                <h4 className="font-medium">{o.name}</h4>
                {o.already_tracking && <span className="text-[10px] bg-forest/10 text-forest px-2 py-0.5 rounded-full font-medium">Tracking</span>}
              </div>
              <p className="text-sm text-ink-light leading-relaxed mb-3">{o.description}</p>
              <div className="text-xs text-ink-lighter">
                Potential annual deduction: <span className="num text-ink">{fmtMoney(o.potential_annual)}</span> ·
                Est. tax savings: <span className="num text-forest font-medium ml-1">{fmtMoney(o.estimated_tax_savings)}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      <ShareTaxSavingsModal
        open={showShare}
        onClose={() => setShowShare(false)}
        taxSaved={summary.tax_savings_from_deductions}
      />
    </div>
  );
}
